//#region IMPORT
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import dayjs from 'dayjs';
import { apiRequest } from '../../../Services/Common/ApiRequest';
//#endregion

/**
 * 유해·위험물질 - MSDS 관리대장 (HAM_BASIC_02) 전용 로직
 * other 케이스에서 사용
 */

// 이미지를 Base64로 변환하는 함수
const ConvertImageToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return '';
    }
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result); // Base64 문자열 반환
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob); // Blob을 Base64로 변환
    });
  } catch (error) {
    console.log(error);
    return '';
  }
};

// URL 인코딩 처리 함수
const fixUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const parts = url.split('/');
  const fileName = parts.pop();
  const encodedFileName = encodeURIComponent(fileName);
  return [...parts, encodedFileName].join('/');
};

// 유해·위험물질 - MSDS 관리대장 조회 함수
export const SearchLogic1 = async (apiUrl, procedureParams, dispatch, setLoading) => {
  setLoading(true);

  try {
    const result = await apiRequest(
      'post',
      apiUrl,
      procedureParams
    );

    let data = result.data;

    if (data.length === 0) {
      dispatch(
        showSnackbar({
          message: '데이터가 없습니다.',
          type: 'info',
        })
      );
      return {
        masterData: [],
        detailData: [],
        printGridData: [],
      };
    } else if (data[0]?.type === 'Error') {
      dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
      return {
        masterData: [],
        detailData: [],
        printGridData: [],
      };
    } else {
      // Master 데이터 추출 (중복 제거)
      const masterDataMap = new Map();
      const detailDataList = [];

      // 원본 데이터에 번호 추가
      const printGridData = Commons.InsertNumber(data);

      data.forEach((item) => {
        if (!item.hbmId) return;

        // Master 데이터가 아직 없는 경우 추가
        if (!masterDataMap.has(item.hbmId)) {
          const masterData = {
            hbmId: item.hbmId,
            processType: item.processType,
            workerName: item.workerName,
            productName: item.productName,
            firstExamMonths: item.firstExamMonths,
            examCycleMonths: item.examCycleMonths,
            distributionContact: item.distributionContact,
            warningLabelFileName: item.warningLabelFileName,
            warningLabelFilePath: item.warningLabelFilePath,
            remark: item.remark,
          };
          masterDataMap.set(item.hbmId, masterData);
        }

        // Detail 데이터 추출 (HBD_ID가 있는 경우)
        if (item.hbdId) {
          const detailData = {
            hbdId: item.hbdId,
            hbmId: item.hbmId,
            substanceName: item.substanceName || '',
            casNo: item.casNo,
            contentPercent: item.contentPercent,
            managementTarget: item.managementTarget || 'N',
            specialManagement: item.specialManagement || 'N',
            workEnvMeasurement: item.workEnvMeasurement || 'N',
            specialHealthExam: item.specialHealthExam || 'N',
            permissionTarget: item.permissionTarget || 'N',
          };

          detailDataList.push(detailData);
        }
      });

      // Master 데이터 번호 추가 및 설정
      const masterArray = Array.from(masterDataMap.values());
      const numberedMasterData = Commons.InsertNumber(masterArray);

      // Detail 데이터 번호 추가 및 설정 (각 마스터별로 1부터 시작)
      const numberedDetailData = [];

      // HBM_ID별로 그룹화
      const detailDataByHbmId = {};

      detailDataList.forEach((detail) => {
        const hbmId = detail.hbmId;
        if (!detailDataByHbmId[hbmId]) {
          detailDataByHbmId[hbmId] = [];
        }
        detailDataByHbmId[hbmId].push(detail);
      });

      // 각 마스터별로 1부터 시작하는 번호 부여
      Object.keys(detailDataByHbmId).forEach((hbmId) => {
        const details = detailDataByHbmId[hbmId];
        details.forEach((detail, index) => {
          numberedDetailData.push({
            ...detail,
            no: index + 1,
          });
        });
      });

      return {
        masterData: numberedMasterData || [],
        detailData: numberedDetailData || [],
        printGridData: printGridData || [],
      };
    }
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return {
      masterData: [],
      detailData: [],
      printGridData: [],
    };
  } finally {
    setLoading(false);
  }
};

// 유해·위험물질 - MSDS 관리대장 인쇄 함수
export const PrintLogic1 = async (
  documentData, // 선택된 항목 데이터 (사용하지 않지만 시그니처 통일을 위해 유지)
  dispatch,
  setLoading,
  rowData1, // 전체 리스트 (사용하지 않음)
  userInfo // 사용자 정보
) => {
  setLoading(true);

  try {
    if (!rowData1 || rowData1.length === 0) {
      dispatch(
        showSnackbar({
          message: '인쇄할 데이터가 없습니다.',
          type: 'warning',
        })
      );
      return null;
    }

    // HeaderData에 오늘 날짜와 사용자 이름 추가
    const today = dayjs().format('YYYY-MM-DD');

    const headerData = {
      currentDate: today,
      userNm: userInfo?.userNm || '',
    };

    // TableData에 그리드 데이터 추가 (경고표지 이미지 포함)
    const tableData = await Promise.all(
      rowData1.map(async (item) => {
        let warningLabelImageBase64 = null;

        // 경고표지 이미지가 있는 경우 Base64로 변환
        const filePath = item.warningLabelFilePath;
        if (filePath) {
          try {
            // URL 인코딩 처리
            const fixedPath = fixUrl(filePath);
            const imageUrl = Global.file.useUrl + 'files/' + fixedPath;

            const base64Image = await ConvertImageToBase64(imageUrl);

            if (base64Image && base64Image.startsWith('data:')) {
              // Base64 문자열 추출
              const cleanBase64 = base64Image.replace(
                /^data:image\/[a-z]+;base64,/,
                ''
              );

              // 순수 Base64만 전달 (다른 파일들과 동일한 방식)
              // 레포트 서버가 Base64 시작 부분으로 이미지 형식을 자동 감지
              warningLabelImageBase64 = cleanBase64;
            } else {
              warningLabelImageBase64 = null;
            }
          } catch (error) {
            console.log(error);
            warningLabelImageBase64 = null;
          }
        }

        return {
          no: item.no || '',
          processType: item.processType || '',
          workerName: item.workerName || '',
          productName: item.productName || '',
          substanceName: item.substanceName || '',
          casNo: item.casNo || '',
          contentPercent: item.contentPercent || '',
          managementTarget: item.managementTarget || '',
          specialManagement: item.specialManagement || '',
          workEnvMeasurement: item.workEnvMeasurement || '',
          specialHealthExam: item.specialHealthExam || '',
          permissionTarget: item.permissionTarget || '',
          firstExamMonths: item.firstExamMonths || '',
          examCycleMonths: item.examCycleMonths || '',
          distributionContact: item.distributionContact || '',
          warningLabelFileName: item.warningLabelFileName || '',
          remark: item.remark || '',
          warningLabelImage: warningLabelImageBase64 || null,
        };
      })
    );

    const printData = {
      FileName: 'HAM_BASIC_03',
      HeaderData: JSON.stringify(headerData),
      TableData: JSON.stringify(tableData),
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    // response.data가 blob인 경우 그대로 반환
    if (response.data instanceof Blob) {
      return response.data;
    }
    // response.data가 다른 형태인 경우 blob으로 변환
    else if (response.data) {
      return new Blob([response.data], { type: 'application/pdf' });
    } else {
      console.error('PrintLogic1: 응답 데이터가 없습니다.');
      return null;
    }
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};
